import React, { useState } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Text,
  Divider,
  SimpleGrid,
  Badge,
  Icon,
  Flex
} from '@chakra-ui/react';
import {
  FiMail,
  FiSend,
  FiCreditCard,
  FiUser,
  FiUserCheck,
  FiSettings,
  FiHelpCircle,
  FiStar,
  FiLock
} from 'react-icons/fi';
import { api } from '../../utils/api';

const EmailTest = () => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');

  const sendTestEmail = async (emailType, buttonLabel) => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast({
        title: 'Email inválido',
        description: 'Por favor, insira um email válido',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    try {
      const response = await api.post('api/email/test/send', {
        emailType,
        recipientEmail
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Email enviado!',
          description: `"${buttonLabel}" enviado para ${recipientEmail}`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        throw new Error(data.message || 'Erro ao enviar email');
      }
    } catch (error) {
      toast({
        title: 'Erro ao enviar',
        description: error.message || 'Erro ao enviar email de teste',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  const EmailButton = ({ emailType, label, colorScheme = 'blue', icon }) => (
    <Button
      leftIcon={icon ? <Icon as={icon} /> : <Icon as={FiMail} />}
      colorScheme={colorScheme}
      onClick={() => sendTestEmail(emailType, label)}
      isLoading={sending}
      loadingText="Enviando..."
      size="sm"
      width="full"
    >
      {label}
    </Button>
  );

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader>
            <Flex align="center" gap={3}>
              <Icon as={FiMail} boxSize={8} color="teal.500" />
              <Box>
                <Heading size="lg">Teste de Emails TOVI</Heading>
                <Text color="gray.600" fontSize="sm" mt={1}>
                  Envie emails de teste para qualquer endereço
                </Text>
              </Box>
            </Flex>
          </CardHeader>
          <CardBody>
            <FormControl>
              <FormLabel>Email de Destino</FormLabel>
              <HStack>
                <Input
                  type="email"
                  placeholder="seu.email@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  size="lg"
                />
                <Badge colorScheme="green" fontSize="md" px={3} py={2}>
                  33+ Tipos
                </Badge>
              </HStack>
            </FormControl>
          </CardBody>
        </Card>

        {/* Subscription Emails */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiCreditCard} color="purple.500" />
              <Heading size="md">Emails de Assinatura (8)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="renewalReminder" label="Lembrete de Renovação" colorScheme="orange" />
              <EmailButton emailType="paymentSuccess" label="Pagamento Confirmado" colorScheme="green" />
              <EmailButton emailType="paymentFailed" label="Falha no Pagamento" colorScheme="red" />
              <EmailButton emailType="subscriptionExpired" label="Assinatura Expirada" colorScheme="gray" />
              <EmailButton emailType="subscriptionCanceled" label="Assinatura Cancelada" colorScheme="yellow" />
              <EmailButton emailType="autoRenewalSuccess" label="Renovação Automática" colorScheme="teal" />
              <EmailButton emailType="gracePeriodStarted" label="Período de Tolerância Iniciado" colorScheme="cyan" />
              <EmailButton emailType="gracePeriodEnding" label="Período de Tolerância Terminando" colorScheme="pink" />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Authentication - Worker */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiUser} color="blue.500" />
              <Heading size="md">Autenticação - Trabalhador (5)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="workerAccountCreated" label="Conta Criada" colorScheme="blue" icon={FiUser} />
              <EmailButton emailType="workerVerificationCode" label="Código de Verificação" colorScheme="purple" icon={FiLock} />
              <EmailButton emailType="workerPasswordRecovery" label="Recuperar Senha" colorScheme="orange" icon={FiLock} />
              <EmailButton emailType="workerPasswordResetSuccess" label="Senha Redefinida" colorScheme="green" icon={FiLock} />
              <EmailButton emailType="workerPasswordChanged" label="Senha Alterada" colorScheme="teal" icon={FiLock} />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Authentication - User */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiUserCheck} color="green.500" />
              <Heading size="md">Autenticação - Usuário (5)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="userAccountCreated" label="Conta Criada" colorScheme="blue" icon={FiUser} />
              <EmailButton emailType="userPasswordRecovery" label="Recuperar Senha" colorScheme="orange" icon={FiLock} />
              <EmailButton emailType="userPasswordResetSuccess" label="Senha Redefinida" colorScheme="green" icon={FiLock} />
              <EmailButton emailType="userPasswordRedefined" label="Senha Redefinida (Admin)" colorScheme="purple" icon={FiLock} />
              <EmailButton emailType="userPasswordChanged" label="Senha Alterada" colorScheme="teal" icon={FiLock} />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Authentication - WebUser */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiSettings} color="red.500" />
              <Heading size="md">Autenticação - Admin (4)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="webUserAccountCreated" label="Conta Admin Criada" colorScheme="red" icon={FiUser} />
              <EmailButton emailType="webUserPasswordRecovery" label="Recuperar Senha Admin" colorScheme="orange" icon={FiLock} />
              <EmailButton emailType="webUserPasswordResetSuccess" label="Senha Admin Redefinida" colorScheme="green" icon={FiLock} />
              <EmailButton emailType="webUserPasswordChanged" label="Senha Admin Alterada" colorScheme="teal" icon={FiLock} />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Service Workflow */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiUserCheck} color="cyan.500" />
              <Heading size="md">Fluxo de Serviços (7)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="workerConfirmationRequest" label="Solicitação Confirmação (Worker)" colorScheme="cyan" />
              <EmailButton emailType="userConfirmationRequest" label="Solicitação Confirmação (User)" colorScheme="cyan" />
              <EmailButton emailType="workerServiceConfirmed" label="Serviço Confirmado (Worker)" colorScheme="green" />
              <EmailButton emailType="userConfirmationThanks" label="Agradecimento Confirmação (User)" colorScheme="green" />
              <EmailButton emailType="rejectionReceived" label="Rejeição Recebida" colorScheme="red" />
              <EmailButton emailType="userCancellation" label="Cancelamento (User)" colorScheme="orange" />
              <EmailButton emailType="workerCancellation" label="Cancelamento (Worker)" colorScheme="orange" />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Support */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiHelpCircle} color="orange.500" />
              <Heading size="md">Suporte (2)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="userNeedHelp" label="Pedido de Ajuda (User)" colorScheme="orange" icon={FiHelpCircle} />
              <EmailButton emailType="workerNeedHelp" label="Pedido de Ajuda (Worker)" colorScheme="orange" icon={FiHelpCircle} />
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Ratings */}
        <Card bg={bgColor} borderColor={borderColor} borderWidth={1}>
          <CardHeader bg={sectionBg}>
            <HStack>
              <Icon as={FiStar} color="yellow.500" />
              <Heading size="md">Avaliações (2)</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <EmailButton emailType="workerRatingThanks" label="Agradecimento Avaliação (Worker)" colorScheme="yellow" icon={FiStar} />
              <EmailButton emailType="userRatingThanks" label="Agradecimento Avaliação (User)" colorScheme="yellow" icon={FiStar} />
            </SimpleGrid>
          </CardBody>
        </Card>

      </VStack>
    </Box>
  );
};

export default EmailTest;
